import * as React from "react";

import {
  AccessReportsAccessAnalysisElementEnum,
  AccessReportsCoverPageElementEnum,
  AccessReportsWizardElement,
  AccessReportsWizardOption,
  AccessReportsWizardProduct,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
} from "@app/access-reports/shared";
import { QESButton, QESRadioButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { getElementByIdFromTemplate } from "../shared/utility";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX } from "react";

interface AccessReportsAdminAddProductDetailsProps {
  disableUserActions: boolean;
  groups: string[];
  hiddenAccessStandards: number[];
  hiddenCalculationMethods: number[];
  hiddenDistanceMethods: number[];
  invalidElements: Map<string, string>;
  onAccessAnalysisDefaultChange: (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum) => void;
  onAccessAnalysisHideChange: (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum) => void;
  onAddGroupClick: (group: string) => void;
  onAllowEditToggle: () => void;
  onProductInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onProductTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onRemoveGroupClick: (group: string) => void;
  onReportTitleInputChange: (reportTitleElement: AccessReportsCoverPageElementEnum, event: React.ChangeEvent<HTMLInputElement>) => void;
  onSkipAccessAnalysisToggle: () => void;
  product: AccessReportsWizardProduct;
  productType: AccessReportsWizardOption;
  productTypes: AccessReportsWizardOption[];
  reportTitleOne: string;
  reportTitleTwo: string;
  reportTitleThree: string;
  template: AccessReportsWizardTemplate;
}

interface AccessReportsAdminAddProductDetailsState {
  group: string;
}

interface ProductDetailsTableData {
  [wafflesKeyColumnName]: string;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

class AccessReportsAdminAddProductDetails extends React.Component<AccessReportsAdminAddProductDetailsProps, AccessReportsAdminAddProductDetailsState> {
  constructor(props: AccessReportsAdminAddProductDetailsProps) {
    super(props);

    this.state = {
      group: "",
    };
  }

  public render() {
    const addGroupValidationText: string = this.getAddGroupValidationText();

    let productTypeValue = "";
    if (!!this.props.productType) {
      productTypeValue = this.props.productType.id.toString();
    }

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <label className="label-m mb-2">
              Product
            </label>
          </div>
        </div>

        <div className="row align-items-end mb-3">
          <div className="col-md-3">
            <QESSelect
              allowEmpty={true}
              disabled={this.props.disableUserActions}
              isValid={!!productTypeValue}
              testId="access-reports-admin__add-product--product-type-dropdown"
              label="Product Type"
              required={true}
              onChange={(e) => this.props.onProductTypeChange(e)}
              value={productTypeValue}
            >
              {this.props.productTypes.map((productType, index) => (
                <option
                  key={index}
                  value={productType.id}
                >
                  {productType.displayName ? productType.displayName : productType.name}
                </option>
              ))}
            </QESSelect>
          </div>

          <div className="col-md-3">
            <QESTextInput
              testId="access-reports-admin__add-product--product-input"
              disabled={this.props.disableUserActions}
              isValid={!!this.props.product.displayName || !!this.props.product.name}
              label="Product"
              onChange={(e) => this.props.onProductInputChange(e)}
              required={true}
              value={(this.props.product.displayName ? this.props.product.displayName : this.props.product.name) || ""}
            />
          </div>

          <div className="col-md-2">
            <QESCheckbox
              checked={this.props.product && this.props.product.editable}
              disabled={this.props.disableUserActions}
              label="Allow Edit"
              testId="access-reports-admin__add-product--allow-edit"
              onChange={() => this.props.onAllowEditToggle()}
            />
          </div>

          <div className="col-md-3">
            <QESCheckbox
              checked={this.props.product?.skipAccessAnalysis}
              disabled={this.props.disableUserActions}
              label="Skip Access Analysis"
              testId="access-reports-admin__add-product--skip-access-analysis"
              onChange={() => this.props.onSkipAccessAnalysisToggle()}
            />
          </div>
        </div>

        <div className="row align-items-top mb-3">
          <div className="col-md-3">
            <QESTextInput
              testId="access-reports-admin__add-product--report-title-one"
              errorText={this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleOne)}
              disabled={this.props.disableUserActions}
              isValid={!this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleOne)}
              label="Default Report Title"
              onChange={(e) => this.props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleOne, e)}
              unrequiredValidation={true}
              value={this.props.reportTitleOne || ""}
            />
          </div>

          <div className="col-md-3">
            <QESTextInput
              testId="access-reports-admin__add-product--report-title-two"
              errorText={this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleTwo)}
              disabled={this.props.disableUserActions}
              isValid={!this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleTwo)}
              label={" ".replace(/ /g, "\u00a0")}
              onChange={(e) => this.props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleTwo, e)}
              unrequiredValidation={true}
              value={this.props.reportTitleTwo || ""}
            />
          </div>

          <div className="col-md-3">
            <QESTextInput
              testId="access-reports-admin__add-product--report-title-three"
              errorText={this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleThree)}
              disabled={this.props.disableUserActions}
              isValid={!this.props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleThree)}
              label={" ".replace(/ /g, "\u00a0")}
              onChange={(e) => this.props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleThree, e)}
              unrequiredValidation={true}
              value={this.props.reportTitleThree || ""}
            />
          </div>
        </div>

        {!this.props.product.skipAccessAnalysis && (
          <React.Fragment>
            <div className="row">
              <div className="col-12">
                <label className="label-m mb-2">
                  Access Analysis
                </label>
              </div>
            </div>

            <div className="row flex-nowrap mb-3">
              <div className="col-4">
                {this.buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.CalculationMethod)}
              </div>
              <div className="col-4">
                {this.buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.DistanceMethod)}
              </div>
              <div className="col-4">
                {this.buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.AccessStandard)}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <label className="label-m mb-2">
                  Analysis Breakdowns
                </label>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <QESTextInput
                  inputAppend={(
                    <QESButton
                      qesType="secondary"
                      className="p-0"
                      disabled={!this.getAddGroupButtonValidity()}
                      qesTestId="access-reports-admin__add-product--add-group"
                      onClick={() => this.onAddGroupClick()}
                    >
                      Add
                    </QESButton>
                  )}
                  testId="access-reports-admin__add-product--group-input"
                  errorText={addGroupValidationText}
                  disabled={this.props.disableUserActions}
                  isValid={!addGroupValidationText}
                  label="Groups"
                  onChange={(e) => this.onGroupInputChange(e)}
                  required={true}
                  value={this.state.group}
                />

                {this.props.groups.length > 0 &&
                  <React.Fragment>
                    <hr />

                    {this.props.groups.map((group, index) => (
                      <QESTextInput
                        inputAppend={(
                          <QESButton
                            className="p-0"
                            disabled={this.props.disableUserActions}
                            qesTestId={`access-reports-admin__add-product--remove-group-${index}`}
                            qesType="secondary-danger"
                            onClick={() => this.props.onRemoveGroupClick(group)}
                          >
                            Remove
                          </QESButton>
                        )}
                        key={index}
                        testId={`access-reports-admin__add-product--readonly-group-${index}`}
                        disabled={this.props.disableUserActions}
                        label={group}
                        hideLabel={true}
                        readOnly
                        value={group}
                      />
                    ))}
                  </React.Fragment>
                }
              </div>
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  private buildAccessAnalysisTable = (accessAnalysisElement: AccessReportsAccessAnalysisElementEnum): React.ReactNode => {
    const element: AccessReportsWizardElement = getElementByIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.AccessAnalysis, accessAnalysisElement);
    const defaultOption: number = element?.options.find(x => x.defaultProducts.includes(this.props.product.id))?.id;
    const title = !!element ? element.title : "";
    const options: AccessReportsWizardOption[] = !!element ? element.options : [];

    let hiddenOptions: number[] = [];

    switch (accessAnalysisElement) {
      case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
        hiddenOptions = this.props.hiddenCalculationMethods;
        break;
      case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
        hiddenOptions = this.props.hiddenDistanceMethods;
        break;
      case AccessReportsAccessAnalysisElementEnum.AccessStandard:
        hiddenOptions = this.props.hiddenAccessStandards;
        break;
      default:
        console.error("Invalid Access Analysis Element Type");
        break;
    }

    const data: ProductDetailsTableData[] = options.map((option) => {
      /* eslint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: option.displayName ? option.displayName : option.name,
        title: option.displayName ? option.displayName : option.name,
        default: (
          <QESRadioButton
            label=""
            className="m-1"
            checked={defaultOption === option.id}
            disabled={hiddenOptions.includes(option.id) || this.props.disableUserActions}
            onChange={() => this.props.onAccessAnalysisDefaultChange(option.id, accessAnalysisElement)}
            id={`access-reports-admin__add-product--default-${accessAnalysisElement}-${option.id}`}
            value={option.id}
            name={`product-option-${accessAnalysisElement}`}
            testId={`access-reports-admin__add-product__${accessAnalysisElement}--${option.name}`}
          />
        ),
        hide: (
          <QESCheckbox
            containerClassName="m-0"
            className="m-1"
            checked={hiddenOptions.includes(option.id)}
            disabled={defaultOption === option.id || this.props.disableUserActions}
            label=""
            testId={`access-reports-admin__add-product--hide-${accessAnalysisElement}-${option.id}`}
            onChange={() => this.props.onAccessAnalysisHideChange(option.id, accessAnalysisElement)}
          />
        ),
      };
      /* eslint-enable */
    });

    const columnConfig = this.getColumnConfig(title);

    if (options.length > 0) {
      return (
        <Waffles<ProductDetailsTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`access-reports-admin-product-details-${title}`}
        />
      );
    }
  }

  private getColumnConfig = (displayTitle: string): ColumnConfig => {
    return {
      default: {
        displayElement: <>Default<span className="text-danger">*</span></>,
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: displayTitle,
        noSort: true,
        testId: "title",
      },
    };
  }

  private getAddGroupButtonValidity = (): boolean => {
    if (!this.props.disableUserActions &&
      !!this.state.group &&
      !this.props.groups.includes(this.state.group)
    ) {
      return true;
    }
  }

  private getAddGroupValidationText = (): string => {
    if (this.props.groups.includes(this.state.group)) {
      return "A group with that name already exists";
    }
  }

  private onAddGroupClick = (): void => {
    this.props.onAddGroupClick(this.state.group);

    this.setState({
      group: "",
    });
  }

  private onGroupInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      group: event.target.value,
    });
  }
}

export { AccessReportsAdminAddProductDetails };
